import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '@/utils/api';

const API = API_URL;

const SEOSettings = () => {
  const [settings, setSettings] = useState({
    google_analytics_id: '',
    google_search_console_tag: '',
    pages: {}
  });
  const [loading, setLoading] = useState(true);

  const pages = [
    { id: 'home', name: 'Home Page' },
    { id: 'pricing', name: 'Pricing' },
    { id: 'how-it-works', name: 'How It Works' },
    { id: 'faqs', name: 'FAQs' },
    { id: 'contact', name: 'Contact' },
    { id: 'terms', name: 'Terms & Conditions' },
    { id: 'complaints', name: 'Complaints Procedure' }
  ];

  useEffect(() => {
    // Fetch settings once on mount. Defined inline so the deps array is honest.
    const load = async () => {
      try {
        const response = await axios.get(`${API}/seo-settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching SEO settings:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
    // One-shot load on mount; refresh is triggered by explicit user actions.
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/seo-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handlePageChange = (pageId, field, value) => {
    setSettings({
      ...settings,
      pages: {
        ...settings.pages,
        [pageId]: {
          ...settings.pages[pageId],
          page_id: pageId,
          [field]: value
        }
      }
    });
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/seo-settings`, settings);
      toast.success('SEO settings saved successfully');
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast.error('Failed to save SEO settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p>Loading SEO settings...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">SEO Settings</h1>

        <Tabs defaultValue="global" className="space-y-6">
          <TabsList>
            <TabsTrigger value="global">Global Settings</TabsTrigger>
            <TabsTrigger value="pages">Page Metadata</TabsTrigger>
          </TabsList>

          {/* Global Settings */}
          <TabsContent value="global">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Global SEO Settings</h2>
              
              <div>
                <Label htmlFor="ga_id">Google Analytics 4 Measurement ID</Label>
                <Input
                  id="ga_id"
                  value={settings.google_analytics_id || ''}
                  onChange={(e) => handleGlobalChange('google_analytics_id', e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Format: G-XXXXXXXXXX</p>
              </div>

              <div>
                <Label htmlFor="gsc_tag">Google Search Console Verification Tag</Label>
                <Input
                  id="gsc_tag"
                  value={settings.google_search_console_tag || ''}
                  onChange={(e) => handleGlobalChange('google_search_console_tag', e.target.value)}
                  placeholder="verification_code"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Enter only the verification code</p>
              </div>

              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                Save Global Settings
              </Button>
            </div>
          </TabsContent>

          {/* Page Metadata */}
          <TabsContent value="pages">
            <div className="space-y-6">
              {pages.map((page) => {
                const pageData = settings.pages[page.id] || {};
                return (
                  <div key={page.id} className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{page.name}</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Page Title</Label>
                        <Input
                          value={pageData.page_title || ''}
                          onChange={(e) => handlePageChange(page.id, 'page_title', e.target.value)}
                          placeholder="Page title (60-70 characters)"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Meta Description</Label>
                        <Textarea
                          value={pageData.meta_description || ''}
                          onChange={(e) => handlePageChange(page.id, 'meta_description', e.target.value)}
                          placeholder="Meta description (150-160 characters)"
                          rows={3}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Canonical URL</Label>
                        <Input
                          value={pageData.canonical_url || ''}
                          onChange={(e) => handlePageChange(page.id, 'canonical_url', e.target.value)}
                          placeholder="https://houseplandirect.com/..."
                          className="mt-1"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Open Graph Title</Label>
                          <Input
                            value={pageData.og_title || ''}
                            onChange={(e) => handlePageChange(page.id, 'og_title', e.target.value)}
                            placeholder="OG title (optional)"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label>Open Graph Image URL</Label>
                          <Input
                            value={pageData.og_image_url || ''}
                            onChange={(e) => handlePageChange(page.id, 'og_image_url', e.target.value)}
                            placeholder="Image URL (optional)"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Open Graph Description</Label>
                        <Textarea
                          value={pageData.og_description || ''}
                          onChange={(e) => handlePageChange(page.id, 'og_description', e.target.value)}
                          placeholder="OG description (optional)"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                Save All Page Metadata
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default SEOSettings;
